import { defineStore } from 'pinia'
import { api } from '@/lib/api'
import { useBoardStore } from '@/stores/board'

const detailRequests = new WeakMap()
const newsTimers = new WeakMap()

// Até quando a pessoa já tinha visto o chamado aberto agora (ver fetchOne).
// null = nunca tinha aberto; undefined = ainda não chegou resposta.
const readUntilFor = { id: null, value: undefined }
const earliest = (a, b) => {
  if (a === undefined) return b
  if (a === null || b === null) return null
  return new Date(a) <= new Date(b) ? a : b
}

export const useTicketsStore = defineStore('tickets', {
  state: () => ({
    records: [],
    meta: { total_count: 0, page: 1, per_page: 25, total_pages: 0 },
    loading: false,
    loaded: false, // cold load vs. refresh (ver board.js)
    current: null,
    loadingOne: false,
    lastParams: {}, // últimos filtros — pro resync do realtime repetir a busca
    news: { atendimento: 0, meus: 0, observando: 0 }, // chamados com novidade (número no menu e no seletor)
    // Quadro e lista do agente: 'mine' (onde é responsável) ou 'watching' (observando).
    view: 'mine',
  }),
  actions: {
    async fetchList(params = {}) {
      this.loading = true
      this.lastParams = params
      try {
        const clean = Object.fromEntries(
          Object.entries({ ...params, view: this.view }).filter(([, v]) => v != null && v !== '')
        )
        const qs = new URLSearchParams(clean).toString()
        const res = await api(`/api/tickets${qs ? `?${qs}` : ''}`)
        if (!res.ok) throw new Error('Falha ao carregar chamados')
        const data = await res.json()
        this.records = data.data
        this.meta = data.meta
        this.loaded = true
      } finally {
        this.loading = false
      }
    },

    // Fechar o chamado: na próxima abertura vale a leitura nova do servidor.
    forgetReadUntil() {
      Object.assign(readUntilFor, { id: null, value: undefined })
    },

    async fetchOne(id) {
      const request = (detailRequests.get(this) || 0) + 1
      detailRequests.set(this, request)
      // Mesmo chamado reaberto → mantém o cache na tela e revalida em silêncio.
      // Chamado diferente → limpa pra não mostrar o anterior enquanto carrega.
      const sameTicket = String(this.current?.id) === String(id)
      this.loadingOne = !sameTicket
      if (!sameTicket) this.current = null
      try {
        // Cada GET marca o chamado como lido e devolve até quando a pessoa já
        // tinha visto. Abrir dispara buscas quase juntas (e as revalidações
        // vêm depois): vale a data mais antiga, senão o "nova" das notas some
        // enquanto a pessoa ainda está lendo.
        if (readUntilFor.id !== String(id)) Object.assign(readUntilFor, { id: String(id), value: undefined })
        const res = await api(`/api/tickets/${id}`)
        const data = res.ok ? await res.json() : null
        if (data && readUntilFor.id === String(id)) readUntilFor.value = earliest(readUntilFor.value, data.read_until)
        if (detailRequests.get(this) !== request) return
        if (!data) throw new Error('Chamado não encontrado')
        data.read_until = readUntilFor.value
        if (detailRequests.get(this) === request) this.current = data
        // Abrir marca como visto no servidor: some o destaque do card e da lista.
        useBoardStore().clearNews(data.id)
        const row = this.records.find(ticket => ticket.id === data.id)
        if (row) row.news = null
        this.fetchNews()
      } catch (error) {
        if (detailRequests.get(this) === request) throw error
      } finally {
        if (detailRequests.get(this) === request) this.loadingOne = false
      }
    },

    // Observadores: admin adiciona/remove; o próprio observador pode sair.
    async addWatcher(id, userId) {
      const res = await api(`/api/tickets/${id}/watchers`, { method: 'POST', body: { user_id: userId } })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.errors?.[0] || 'Não foi possível adicionar o observador')
      if (this.current?.id === Number(id)) this.current = { ...this.current, watchers: data.watchers }
    },
    async removeWatcher(id, userId) {
      const res = await api(`/api/tickets/${id}/watchers/${userId}`, { method: 'DELETE' })
      if (!res.ok) throw new Error('Não foi possível remover o observador')
      const data = await res.json()
      if (this.current?.id === Number(id)) this.current = { ...this.current, watchers: data.watchers }
    },

    // Rajada de eventos (realtime) vira uma busca só.
    fetchNews() {
      clearTimeout(newsTimers.get(this))
      newsTimers.set(this, setTimeout(async () => {
        const res = await api('/api/tickets/news').catch(() => null)
        if (res?.ok) this.news = await res.json()
      }, 300))
    },

    async update(id, changes) {
      const res = await api(`/api/tickets/${id}`, {
        method: 'PATCH',
        body: { ticket: changes },
      })
      if (!res.ok) throw new Error('Falha ao atualizar o chamado')
      const updated = await res.json()
      const index = this.records.findIndex(ticket => ticket.id === updated.id)
      if (index >= 0) this.records[index] = { ...this.records[index], ...updated }
      if (this.current?.id === updated.id) this.current = updated
      return updated
    },

    // Abre a issue do chamado no GitLab ({ title, description }).
    async linkIssue(id, body) {
      const res = await api(`/api/tickets/${id}/gitlab_issue`, { method: 'POST', body })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.errors?.[0] || 'Não foi possível abrir a issue')
      await this.fetchOne(id)
      return data.gitlab_issue
    },
    async unlinkIssue(id) {
      const res = await api(`/api/tickets/${id}/gitlab_issue`, { method: 'DELETE' })
      if (!res.ok) throw new Error('Não foi possível desvincular a issue')
      await this.fetchOne(id)
    },
    async resendComment(ticketId, commentId) {
      const res = await api(`/api/tickets/${ticketId}/comments/${commentId}/resend`, { method: 'POST' })
      if (!res.ok) throw new Error('Falha ao reenviar')
      await this.fetchOne(ticketId)
    },

    async addComment(id, comment, files = []) {
      // Com anexo vai multipart; sem, JSON como sempre.
      let body = { comment }
      if (files.length) {
        body = new FormData()
        body.append('comment[body]', comment.body)
        body.append('comment[kind]', comment.kind)
        files.forEach(file => body.append('attachments[]', file))
      }
      const res = await api(`/api/tickets/${id}/comments`, { method: 'POST', body })
      if (!res.ok) throw new Error('Falha ao comentar')
      // Recarrega pra trazer o comentário + evento na timeline.
      await this.fetchOne(id)
    },

    // Realtime (ActionCable): upsert/remove na lista conforme a relevância.
    // Retorna 'added' | 'updated' | 'removed' | 'none'.
    applyRealtime(ticket, auth) {
      // Observando: quem saiu/entrou como observador não vem no aviso; refaz a busca.
      if (this.view === 'watching' || auth.isAdmin) {
        this.fetchList(this.lastParams)
        return 'none'
      }
      const relevant = auth.isAdmin || ticket.assignee_id === auth.user?.id
      const idx = this.records.findIndex(r => r.id === ticket.id)
      let action = 'none'

      if (relevant) {
        if (idx >= 0) {
          this.records[idx] = { ...this.records[idx], ...ticket }
          action = 'updated'
        } else {
          this.records = [ticket, ...this.records]
          this.meta.total_count += 1
          action = 'added'
        }
      } else if (idx >= 0) {
        this.records.splice(idx, 1)
        this.meta.total_count = Math.max(0, this.meta.total_count - 1)
        action = 'removed'
      }

      // Detalhe aberto desse chamado → recarrega pra refletir a mudança.
      if (this.current?.id === ticket.id) this.fetchOne(ticket.id)
      return action
    },

    refreshIfCurrent(id) {
      if (this.current?.id === id) this.fetchOne(id)
    },

    // Resync pós-reconexão do WebSocket: repete a última busca (mesmos filtros)
    // e revalida o chamado aberto — eventos perdidos no vão não são re-entregues.
    async resync() {
      if (this.loaded) await this.fetchList(this.lastParams)
      if (this.current) await this.fetchOne(this.current.id)
    },
  },
})
